import { useMemo } from "react";
import { UIPrice, UIUnifiedPriceBreakdownAttachedAncillary } from "@flights/types";
import { StackProps, TextProps } from "@bookingcom/bui-react";
import useFormatPrice from "../../../hooks/useFormatPrice";
import useUserAgent from "../../../hooks/useUserAgent";
import { pricingUtils } from "@flights/web-api-utils-universal";
import { type UnifiedPriceTagPriceBreakdownProps } from "./UnifiedPriceTagPriceBreakdown";
import useRouteName from "../../../hooks/useRouteName";

export type UnifiedPriceTagAlignment = "start" | "end" | "space-between";

export type UnifiedPriceTagPosition = "top" | "bottom" | "start" | "end";

export type UnifiedPriceTagControllerProps = {
  productId?: string;

  price: UIPrice;
  priceProps?: Pick<TextProps, "attributes" | "className" | "tagName" | "variant">;
  priceAlignment?: UnifiedPriceTagAlignment;
  priceTranslations?: ((formattedPrice: string) => string)[];
  priceLabel?: string;

  pricePerAdult?: UIPrice;
  pricePerAdultTranslations?: ((formattedPrice: string) => string)[];
  pricePerAdultExplanationTranslations?: ((formattedPrice: string) => string)[];
  pricePerAdultExplanationProps?: Pick<TextProps, "attributes" | "className" | "variant">;
  shouldShowPricePerAdult?: boolean;

  strikethroughPrice?: UIPrice;
  strikethroughPriceProps?: Pick<TextProps, "variant">;
  strikethroughPricePosition?: UnifiedPriceTagPosition;

  priceExplanation?: string;
  priceExplanationProps?: Pick<TextProps, "attributes" | "className" | "variant">;
  shouldShowPriceExplanationOnly?: boolean;

  shouldRoundPrice?: boolean;

  attachedAncillaries?: UIUnifiedPriceBreakdownAttachedAncillary[];
} & UnifiedPriceTagPriceBreakdownProps;

export const useUnifiedPriceTagController = (props: UnifiedPriceTagControllerProps) => {
  const { isMobile } = useUserAgent();
  const routeName = useRouteName();
  const { formatPrice } = useFormatPrice();

  const {
    price,
    priceAlignment,
    priceTranslations = [],
    pricePerAdult,
    pricePerAdultExplanationTranslations = [],
    pricePerAdultTranslations = [],
    priceProps,
    shouldRoundPrice,
    shouldShowPricePerAdult = false,
    strikethroughPrice,
    strikethroughPricePosition = "top"
  } = props;

  const formattedPrice = useMemo((): string => {
    const priceValue = shouldShowPricePerAdult && pricePerAdult ? pricePerAdult : price;
    const priceFormatted = formatPrice(priceValue, shouldRoundPrice ? { decimalPlaces: 0 } : {});

    return (shouldShowPricePerAdult && pricePerAdult ? pricePerAdultTranslations : priceTranslations).reduce(
      (acc, translate) => translate(acc),
      priceFormatted
    );
  }, [
    price,
    pricePerAdult,
    priceTranslations,
    pricePerAdultTranslations,
    shouldShowPricePerAdult,
    shouldRoundPrice,
    formatPrice
  ]);

  const formattedStrikethroughPrice = useMemo((): string => {
    return !!strikethroughPrice ? formatPrice(strikethroughPrice, shouldRoundPrice ? { decimalPlaces: 0 } : {}) : "";
  }, [formatPrice, shouldRoundPrice, strikethroughPrice]);

  const priceWrapperStackProps = useMemo(
    (): StackProps => ({
      justifyContent: priceAlignment || "start"
    }),
    [priceAlignment]
  );

  const priceStackProps = useMemo((): StackProps => {
    if (!strikethroughPrice) {
      return {};
    }

    const direction: StackProps["direction"] =
      strikethroughPricePosition === "top"
        ? "column"
        : strikethroughPricePosition === "bottom"
        ? "column-reverse"
        : strikethroughPricePosition === "start"
        ? "row"
        : "row-reverse";
    const alignItems = ["top", "bottom"].includes(strikethroughPricePosition)
      ? priceAlignment === "end"
        ? priceAlignment
        : "start"
      : "baseline";

    const getGapValue = () => {
      if (["top", "bottom"].includes(strikethroughPricePosition)) return 0;
      if (routeName.includes("flightdetails")) return isMobile ? 0 : 2;
      return 1;
    };
    const gap = getGapValue();

    return { ...priceProps, direction, alignItems, gap };
  }, [priceAlignment, priceProps, strikethroughPrice, strikethroughPricePosition, isMobile, routeName]);

  const priceTextProps = useMemo(
    (): TextProps => ({
      variant: priceProps?.variant || "strong_1",
      tagName: priceProps?.tagName || "div"
    }),
    [priceProps]
  );

  const pricePerAdultExplanation = useMemo((): string => {
    const priceFormatted = formatPrice(price, shouldRoundPrice ? { decimalPlaces: 0 } : {});

    return pricePerAdultExplanationTranslations.reduce((acc, translate) => translate(acc), priceFormatted);
  }, [formatPrice, price, pricePerAdultExplanationTranslations, shouldRoundPrice]);

  const pricePerAdultExplanationStrikethroughInline = useMemo(
    (): boolean => !(isMobile && pricingUtils.isPriceWithLargeNumber(price)),
    [isMobile, price]
  );

  return {
    formattedPrice,
    formattedStrikethroughPrice,
    priceWrapperStackProps,
    priceStackProps,
    priceTextProps,
    pricePerAdultExplanation,
    pricePerAdultExplanationStrikethroughInline
  };
};
